import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  Button,
  Label,
  TextInput,
  Select,
  Textarea,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "flowbite-react";
import { countyLinksThunks, countiesThunks } from "../../app/store";

const LINK_TYPES = [
  "AUCTION_SITE",
  "CLERK_OFFICE",
  "PUBLIC_RECORDS_SEARCH",
  "PROPERTY_APPRAISER",
  "TAX_COLLECTOR",
  "GIS_MAP",
  "FEMA",
  "OTHER",
];

function emptyLink() {
  return {
    id: null,
    link_type: "AUCTION_SITE",
    url: "",
    label: "",
    is_primary: false,
  };
}

export default function CountyFormModal({
  open,
  onClose,
  initialData,
  onSubmit,
  saving,
  states = [],
  regions = [],
}) {
  const isEdit = !!initialData?.id;
  const dispatch = useDispatch();

  const defaults = useMemo(
    () => ({
      state_id: "",
      region_id: "",
      name: "",
      notes: "",
      appraiser_rules_json: "",
    }),
    [],
  );

  const [form, setForm] = useState(defaults);
  const [touched, setTouched] = useState({});
  const [links, setLinks] = useState([]);

  function setField(key, value) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function markTouched(key) {
    setTouched((t) => ({ ...t, [key]: true }));
  }

  function setLink(idx, patch) {
    setLinks((prev) => prev.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  }

  function addLink() {
    setLinks((prev) => [...prev, emptyLink()]);
  }

  function removeLink(idx) {
    setLinks((prev) => prev.filter((_, i) => i !== idx));
  }

  function setPrimary(idx) {
    setLinks((prev) =>
      prev.map((x, i) => ({
        ...x,
        is_primary: i === idx,
      })),
    );
  }

  useEffect(() => {
    if (!open) return;

    if (!initialData) {
      setForm(defaults);
      setTouched({});
      setLinks([]);
      return;
    }

    setForm({
      state_id: initialData.state_id ?? "",
      region_id: initialData.region_id ?? "",
      name: initialData.name ?? "",
      notes: initialData.notes ?? "",
      appraiser_rules_json: initialData.appraiser_rules_json ?? "",
    });

    const existing = Array.isArray(initialData?.links) ? initialData.links : [];
    const mapped = existing.map((l) => ({
      id: l.id ?? null,
      link_type: l.link_type ?? "AUCTION_SITE",
      url: l.url ?? "",
      label: l.label ?? "",
      is_primary: !!l.is_primary,
    }));

    setLinks(mapped);
    setTouched({});
  }, [open, initialData, defaults]);

  const jsonError = useMemo(() => {
    const raw = form.appraiser_rules_json?.trim();
    if (!raw) return null;
    try {
      JSON.parse(raw);
      return null;
    } catch {
      return "JSON invalido";
    }
  }, [form.appraiser_rules_json]);

  const errors = {
    state_id: !form.state_id ? "Obrigatorio" : null,
    name: !form.name?.trim() ? "Obrigatorio" : null,
    appraiser_rules_json: jsonError,
  };

  const hasErrors = !!(errors.state_id || errors.name || errors.appraiser_rules_json);

  async function saveLinks(countyId) {
    const toSave = links
      .map((l) => ({
        ...l,
        url: l.url?.trim() || "",
        label: l.label?.trim() || "",
      }))
      .filter((l) => !!l.url);

    for (const l of toSave) {
      const payload = {
        county_id: countyId,
        link_type: l.link_type,
        url: l.url,
        label: l.label ? l.label : null,
        is_primary: !!l.is_primary,
      };

      if (l.id) {
        await dispatch(countyLinksThunks.updateOne({ id: l.id, payload }));
      } else {
        await dispatch(countyLinksThunks.createOne(payload));
      }
    }

    await dispatch(countiesThunks.getById(countyId));
  }

  function handleSave() {
    setTouched({ state_id: true, name: true, appraiser_rules_json: true });
    if (hasErrors) return;

    const countyPayload = {
      state_id: form.state_id,
      region_id: form.region_id ? form.region_id : null,
      name: form.name.trim(),
      notes: form.notes?.trim() ? form.notes.trim() : null,
      appraiser_rules_json: form.appraiser_rules_json?.trim()
        ? form.appraiser_rules_json.trim()
        : null,
    };

    onSubmit(countyPayload);

    if (isEdit && initialData?.id) {
      (async () => {
        await saveLinks(initialData.id);
      })();
    }
  }

  return (
    <Modal show={open} onClose={onClose}>
      <ModalHeader>{isEdit ? "Editar County" : "Novo County"}</ModalHeader>

      <ModalBody>
        <div className="space-y-4">
          <div>
            <Label htmlFor="state_id" value="State" />
            <Select
              id="state_id"
              value={form.state_id}
              onChange={(e) => setField("state_id", e.target.value)}
              onBlur={() => markTouched("state_id")}
              disabled={saving === true}
            >
              <option value="">Selecione...</option>
              {states.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.code} - {st.name}
                </option>
              ))}
            </Select>

            {touched.state_id && errors.state_id ? (
              <p className="mt-1 text-sm text-red-600">{errors.state_id}</p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="region_id" value="District/Region (opcional)" />
            <Select
              id="region_id"
              value={form.region_id}
              onChange={(e) => setField("region_id", e.target.value)}
              disabled={saving === true}
            >
              <option value="">Sem distrito</option>
              {regions.map((rg) => (
                <option key={rg.id} value={rg.id}>
                  {rg.name}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              Se selecionar um distrito, ele precisa existir.
            </p>
          </div>

          <div>
            <Label htmlFor="name" value="Name" />
            <TextInput
              id="name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              onBlur={() => markTouched("name")}
              placeholder="Orange County"
              color={touched.name && errors.name ? "failure" : "gray"}
              helperText={touched.name && errors.name ? errors.name : null}
              disabled={saving === true}
            />
          </div>

          <div>
            <Label htmlFor="notes" value="Notes" />
            <Textarea
              id="notes"
              rows={4}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Observacoes do county..."
              disabled={saving === true}
            />
          </div>

          <div>
            <Label htmlFor="appraiser_rules_json" value="Appraiser Rules JSON (opcional)" />
            <Textarea
              id="appraiser_rules_json"
              rows={6}
              value={form.appraiser_rules_json}
              onChange={(e) => setField("appraiser_rules_json", e.target.value)}
              placeholder='{"url_template":"https://site.com?parcel={{parcel_number}}"}'
              disabled={saving === true}
            />
            {touched.appraiser_rules_json && errors.appraiser_rules_json ? (
              <p className="mt-1 text-sm text-red-600">{errors.appraiser_rules_json}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Use placeholders: {"{{parcel_number}}"}, {"{{property_address}}"}, {"{{alt_key}}"}.
              </p>
            )}
          </div>

          {isEdit ? (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Links do County
                </p>
                <Button color="gray" size="xs" onClick={addLink} disabled={saving === true}>
                  Adicionar link
                </Button>
              </div>

              {links?.length ? (
                <div className="space-y-3">
                  {links.map((l, idx) => {
                    const canRemove = !l.id;
                    return (
                      <div
                        key={l.id || idx}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <Label value="Type" />
                            <Select
                              value={l.link_type}
                              onChange={(e) => setLink(idx, { link_type: e.target.value })}
                              disabled={saving === true}
                            >
                              {LINK_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </Select>
                          </div>

                          <div className="md:col-span-2">
                            <Label value="URL" />
                            <TextInput
                              value={l.url}
                              onChange={(e) => setLink(idx, { url: e.target.value })}
                              placeholder="https://..."
                              disabled={saving === true}
                            />
                          </div>

                          <div>
                            <Label value="Label (opcional)" />
                            <TextInput
                              value={l.label}
                              onChange={(e) => setLink(idx, { label: e.target.value })}
                              placeholder="Ex: site oficial"
                              disabled={saving === true}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <input
                              type="checkbox"
                              checked={!!l.is_primary}
                              onChange={() => setPrimary(idx)}
                              disabled={saving === true}
                            />
                            Primary
                          </label>

                          {canRemove ? (
                            <Button
                              color="gray"
                              size="xs"
                              onClick={() => removeLink(idx)}
                              disabled={saving === true}
                            >
                              Remover
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-500">
                              (Remocao ainda nao implementada)
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Nenhum link cadastrado. Clique em "Adicionar link".
                </p>
              )}
            </div>
          ) : null}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          className="text-slate-600"
          onClick={handleSave}
          disabled={saving === true}
        >
          {saving === true ? "Salvando..." : "Salvar"}
        </Button>
        <Button color="gray" onClick={onClose} disabled={saving === true}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
